import { useCallback, useEffect, useState } from 'react';
import { formatEther, formatUnits, isAddress, parseEther, type Address } from 'viem';
import { ranchNftAbi } from './contract';
import { projectTokenAbi, ranchIdoAbi } from './projectTokenContract';
import { nstChain, publicClient, walletClient } from './wallet';

type Props = { address: Address; notify: (message: string) => void; fail: (error: unknown) => void };
type LaunchState = {
  name: string; symbol: string; decimals: number; balance: bigint; rewardPerNft: bigint;
  tokensPerNative: bigint; startTime: bigint; endTime: bigint; raised: bigint; hardCap: bigint; contributed: bigint;
};

export function ProjectTokenPanel({ address, notify, fail }: Props) {
  const tokenAddress = import.meta.env.VITE_PROJECT_TOKEN_ADDRESS as Address | undefined;
  const idoAddress = import.meta.env.VITE_IDO_CONTRACT_ADDRESS as Address | undefined;
  const nftAddress = import.meta.env.VITE_NFT_CONTRACT_ADDRESS as Address | undefined;
  const swapTemplate = import.meta.env.VITE_SWAP_URL as string | undefined;
  const configured = Boolean(tokenAddress && idoAddress && isAddress(tokenAddress) && isAddress(idoAddress));
  const [state, setState] = useState<LaunchState | null>(null);
  const [amount, setAmount] = useState('0.1');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!configured || !tokenAddress || !idoAddress) return;
    const [name, symbol, decimals, balance, tokensPerNative, startTime, endTime, raised, hardCap, contributed] = await Promise.all([
      publicClient.readContract({ address: tokenAddress, abi: projectTokenAbi, functionName: 'name' }),
      publicClient.readContract({ address: tokenAddress, abi: projectTokenAbi, functionName: 'symbol' }),
      publicClient.readContract({ address: tokenAddress, abi: projectTokenAbi, functionName: 'decimals' }),
      publicClient.readContract({ address: tokenAddress, abi: projectTokenAbi, functionName: 'balanceOf', args: [address] }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'tokensPerNative' }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'startTime' }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'endTime' }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'raised' }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'hardCap' }),
      publicClient.readContract({ address: idoAddress, abi: ranchIdoAbi, functionName: 'contributed', args: [address] })
    ]);
    let rewardPerNft = 0n;
    if (nftAddress && isAddress(nftAddress)) {
      rewardPerNft = await publicClient.readContract({ address: nftAddress, abi: ranchNftAbi, functionName: 'rewardPerNft' }).catch(() => 0n);
    }
    setState({ name, symbol, decimals, balance, rewardPerNft, tokensPerNative, startTime, endTime, raised, hardCap, contributed });
  }, [address, configured, idoAddress, nftAddress, tokenAddress]);

  useEffect(() => { load().catch(fail); }, [load]);

  if (!configured) {
    return <section className="token-launch unconfigured"><div><small>PROJECT TOKEN</small><h3>项目代币标准接口已预留</h3><p>项目方部署 ERC‑20 与 IDO 后，只需填写环境变量地址，无需修改游戏源码。</p></div></section>;
  }

  const now = BigInt(Math.floor(Date.now() / 1000));
  const live = Boolean(state && now >= state.startTime && now <= state.endTime && state.raised < state.hardCap);
  const swapUrl = swapTemplate?.replaceAll('{token}', tokenAddress!).replaceAll('{chainId}', String(nstChain.id));
  const estimate = state && Number(amount) > 0 ? formatUnits(parseEther(amount || '0') * state.tokensPerNative / parseEther('1'), state.decimals) : '0';

  async function buy() {
    if (!idoAddress || !state) return;
    const value = parseEther(amount);
    if (value <= 0n) throw new Error('请输入有效的参与数量');
    setBusy(true);
    try {
      const hash = await walletClient(address).writeContract({
        address: idoAddress, abi: ranchIdoAbi, functionName: 'buy', value,
        account: address, chain: nstChain, gasPrice: await publicClient.getGasPrice()
      });
      const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 180_000 });
      if (receipt.status !== 'success') throw new Error('IDO 交易执行失败');
      await load();
      notify(`IDO 参与成功，获得约 ${estimate} ${state.symbol}`);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }

  return <section className="token-launch">
    <div className="token-launch-head"><div><small>PROJECT TOKEN · ERC-20</small><h3>{state?.name ?? '正在读取项目代币'}</h3></div><b>{state?.symbol ?? '--'}</b></div>
    {state && <>
      <div className="token-launch-stats"><div><small>钱包余额</small><b>{formatUnits(state.balance, state.decimals)} {state.symbol}</b></div><div><small>NFT 单枚赠送</small><b>{formatUnits(state.rewardPerNft, state.decimals)} {state.symbol}</b></div><div><small>IDO 进度</small><b>{formatEther(state.raised)} / {formatEther(state.hardCap)} {nstChain.nativeCurrency.symbol}</b></div><div><small>我的参与</small><b>{formatEther(state.contributed)} {nstChain.nativeCurrency.symbol}</b></div></div>
      <label className="ido-input">参与 IDO（{nstChain.nativeCurrency.symbol}）<div><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /><span>≈ {estimate} {state.symbol}</span></div></label>
      <button className="primary large" disabled={!live || busy} onClick={() => buy().catch(fail)}>{busy ? '等待钱包确认…' : live ? '参与项目代币 IDO' : 'IDO 当前未开放'}</button>
      <div className="token-launch-links"><button onClick={() => load().catch(fail)}>刷新链上数据</button>{swapUrl && <a href={swapUrl} target="_blank" rel="noreferrer">前往 Swap / LP ↗</a>}</div>
    </>}
    <p>代币名称、符号、供应量、IDO 比例、上下限、时间和 Swap 地址均由项目方部署参数配置。</p>
  </section>;
}
